import PropTypes from "prop-types";
import { Fragment, useCallback, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import AlertMessage from "app/pages/.shared/AlertMessage";
import { propTypes, reduxForm } from "redux-form";
import Input from "app/pages/.shared/form/Input";
import Button from "app/pages/.shared/form/Button";
import { ALERT_TYPE, KEY_CODES, PROMO_CODE_STATUS } from "app/constants";
import IconInsuranceChecked from "app/pages/.shared/static/icons/IconInsuranceChecked";
import AmountContainer from "app/pages/.shared/AmountContainer";
import AmountSign from "app/pages/.shared/AmountSign/AmountSign";
import "./PromotionCode.scss";

const PromotionCode = ({
	fields,
	handleSubmit,
	submitting,
	status,
	message,
	submit = true,
	disabled = false,
	promotionValue = 0,
	promotionCode,
	initializeForm,
	formInitialValues,
	showAmountInButton,
	onFocus,
}) => {
	let promoCodeMessage;

	if (status === PROMO_CODE_STATUS.SUCCESS || promotionValue > 0) {
		promoCodeMessage = (
			<Fragment>
				<div className="promotion-code__message promotion-code__message--success">
					<IconInsuranceChecked width={14} height={12} style={{ marginRight: "5px" }} />
					<FormattedMessage id="promotion.code.success.message" />
				</div>
				{message && <AlertMessage message={message} />}
			</Fragment>
		);
	} else if (status === PROMO_CODE_STATUS.INVALID_PROMOCODE) {
		promoCodeMessage = (
			<span className="promotion-code__message promotion-code__message--error">
				{message ? (
					<AlertMessage message={message} alertType={ALERT_TYPE.ERROR} />
				) : (
					<AlertMessage
						message={<FormattedMessage id="promotion.code.error.message" />}
						alertType={ALERT_TYPE.ERROR}
					/>
				)}
			</span>
		);
	}

	useEffect(() => {
		initializeForm(formInitialValues);
	}, [promotionCode]);

	const handleOnEnterKeyPress = useCallback(event => {
		if (event.keyCode === KEY_CODES.ENTER && !submit) {
			handleSubmit();
		}
	}, []);

	let successButton = (
		<Button
			disabled={disabled}
			variant="primary"
			onClick={!submit ? handleSubmit : undefined}
			submit={submit}
			loading={submitting}
			className="promotion-code__button"
			data-testid="promocode-submit-button"
		>
			<FormattedMessage id="promotion.code.submit.label" />
		</Button>
	);

	if (status === PROMO_CODE_STATUS.SUCCESS || promotionValue > 0) {
		successButton = showAmountInButton ? (
			<Button
				variant="primary"
				submit={submit}
				loading={submitting}
				className="promotion-code__button promotion-code__button--disabled"
			>
				<AmountContainer amount={promotionValue} prefix={<AmountSign sign={"-"} />} />
			</Button>
		) : (
			<Button
				variant="primary"
				onClick={!submit ? handleSubmit : undefined}
				submit={submit}
				loading={submitting}
				className="promotion-code__button"
				data-testid="promocode-cancel-button"
			>
				<FormattedMessage id="cancel.action" />
			</Button>
		);
	} else if (status === PROMO_CODE_STATUS.INVALID_PROMOCODE) {
		successButton = showAmountInButton ? null : (
			<Button
				variant="primary"
				onClick={!submit ? handleSubmit : undefined}
				submit={submit}
				loading={submitting}
				className="promotion-code__button"
				data-testid="promocode-cancel-button"
			>
				<FormattedMessage id="cancel.action" />
			</Button>
		);
	}

	return (
		<form
			className="promotion-code"
			data-testid="promotion-code"
			onSubmit={submit ? handleSubmit : undefined}
		>
			<div className="promotion-code__form">
				<div className="promotion-code__input">
					<Input
						disabled={
							status === PROMO_CODE_STATUS.SUCCESS ||
							status === PROMO_CODE_STATUS.INVALID_PROMOCODE
						}
						field={fields.promoCode}
						name="promotion-code"
						id="promotion-code"
						label={<FormattedMessage id="promotion.code.label" />}
						data-testid="promocode-input"
						onFocus={onFocus}
						onKeyDown={handleOnEnterKeyPress}
					/>
					{showAmountInButton &&
						(status === PROMO_CODE_STATUS.INVALID_PROMOCODE ||
							status === PROMO_CODE_STATUS.SUCCESS ||
							promotionValue > 0) && (
							<i
								className="icon icon--cross-dark"
								onClick={!submit ? handleSubmit : undefined}
								data-testid="promocode-cancel-button"
							/>
						)}
				</div>

				{successButton}
			</div>
			{promoCodeMessage}

			{disabled && (
				<div className="promotion-code__message promotion-code__message--disabled">
					<FormattedMessage id="promotion.code.disabled.credits" />
				</div>
			)}
		</form>
	);
};

const reduxFormConfig = {
	form: "promotion-code-form",
	fields: ["promoCode"],
};

PromotionCode.propTypes = {
	...propTypes,
	submit: PropTypes.bool,
	status: PropTypes.string,
	message: PropTypes.string,
	promotionValue: PropTypes.number,
	promotionCode: PropTypes.string,
	showAmountInButton: PropTypes.bool,
};

PromotionCode.defaultProps = {
	formInitialValues: {},
	onFocus: () => {},
};

export default reduxForm(reduxFormConfig)(PromotionCode);
